/**
 * `/api/inbox` — your own notifications.
 *
 * Separate from `notifications.ts`, which is the *administration* of channels
 * and routing rules and needs `notification.read`. This needs nothing but a
 * session. Two meanings of one word, and mixing them in a single module is how
 * a screen ends up gated on a permission it should never have asked for.
 *
 * Note what is absent: no function here takes a user id. The person is the
 * session, on the server side, always.
 */
import type {
  InboxItemResponse,
  InboxListResponse,
  ListQueryParams,
  MarkAllReadResponse,
  UnreadCountResponse,
} from '@forsys/shared';
import { apiFetch, query } from './client';

export interface InboxParams extends ListQueryParams {
  /** Only what has not been read yet. */
  unread?: boolean;
}

export function listInbox(params: InboxParams = {}): Promise<InboxListResponse> {
  return apiFetch<InboxListResponse>(`/inbox${query({ ...params })}`);
}

/**
 * The badge.
 *
 * The one endpoint in this application that is polled, so it is deliberately
 * the smallest: a single integer, and no page of rows to build a count from.
 */
export function unreadCount(): Promise<UnreadCountResponse> {
  return apiFetch<UnreadCountResponse>('/inbox/unread-count');
}

export function markRead(id: number): Promise<InboxItemResponse> {
  return apiFetch<InboxItemResponse>(`/inbox/${id}/read`, { method: 'POST' });
}

export function markUnread(id: number): Promise<InboxItemResponse> {
  return apiFetch<InboxItemResponse>(`/inbox/${id}/unread`, { method: 'POST' });
}

export function dismiss(id: number): Promise<InboxItemResponse> {
  return apiFetch<InboxItemResponse>(`/inbox/${id}/dismiss`, { method: 'POST' });
}

export function markAllRead(): Promise<MarkAllReadResponse> {
  return apiFetch<MarkAllReadResponse>('/inbox/read-all', { method: 'POST' });
}
